/* Core & libraries */
import React, { useState, useEffect } from "react";
import { useTheme } from "react-jss";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import { FormattedMessage } from "react-intl";
import moment from "helpers/moment";

/* Helpers, constants, custom methods, internal libraries */
import { useDiscountService } from "../../services/discountService";
import { useHomepageService } from "../../services/homepageService";
import { useLocaleContext } from "../../contexts/localeContext";

/* Components */
import DiscountsContent from "../../components/discountsContent";
import DiscountsSkeleton from "../../components/discountsContent/skeleton";

/* Styles */
import styles from "./styles";

const Discounts = () => {
  const theme = useTheme();
  const classes = styles({ theme });

  const globalLocale = useLocaleContext();
  const { getDiscountsDetails } = useDiscountService();
  const { staticContent } = useHomepageService();
  const [beResponse, setBeResponse] = useState(null);

  const fetchDiscounts = async () => {
    setBeResponse(null);
    const response = await getDiscountsDetails();
    setBeResponse(response);
  };

  useEffect(() => {
    fetchDiscounts();
  }, []);

  const transformDiscountData = (beResponse, contentData) => {
    const formatDate = (dateString) => {
      return moment(dateString || `${new Date().getFullYear() + 1}-12-31`)
        .locale(globalLocale)
        .format("LL");
    };
    const tierContentMap = new Map(
      contentData?.discounts.map((tier) => [tier.tierId, tier])
    );

    const createContentItem = (tierLevel) => {
      const tierContent = tierContentMap.get(tierLevel.tierId);
      if (!tierContent) return null;

      return {
        title: (
          <FormattedMessage
            id="discounts.content.benefitsTitle"
            values={{ title: tierContent.title }}
          />
        ),
        description: !tierLevel.managedTier ? (
          <FormattedMessage
            id="discounts.content.benefitsDescription"
            values={{
              required: new Intl.NumberFormat("en-US").format(
                tierLevel.minimumSpend
              ),
            }}
          />
        ) : (
          ""
        ),
        activeIcons: !tierLevel.isLocked,
        allowUnlock: tierLevel.allowUnlock,
        benefits: tierContent.benefits,
        tierCode: tierLevel.tierCode,
        discountCode: tierLevel.discountCode,
        endDate: tierLevel.endDate,
      };
    };

    const result = {
      currentSpend: beResponse.currentSpend,
      contentData: [],
    };

    // CASE 1: No current tier
    if (!beResponse.currentTier) {
      const lowestTier = beResponse.nextTier;
      const lowestTierContent = lowestTier
        ? tierContentMap.get(lowestTier.tierId)
        : null;

      Object.assign(result, {
        currentTier: false,
        tierToUnlock: lowestTierContent?.title || null,
        valueToUnlock: lowestTier?.minimumSpend || null,
        displayedTierDate: lowestTier ? formatDate(lowestTier.endDate) : null,
        tierStatus: lowestTier?.tierStatus || null,
        noTierTitle: contentData?.noTierTitle,
        pdfPath: contentData?.noTierPdfPath,
      });

      beResponse?.tierLevels?.forEach((tierLevel) => {
        const contentItem = createContentItem(tierLevel);
        if (contentItem) result.contentData.push(contentItem);
      });

      return result;
    }

    // CASE 2: Has current tier
    const currentTierContent = tierContentMap.get(
      beResponse.currentTier.tierId
    );
    const nextHigherTier = beResponse.nextTier;
    const nextHigherTierContent = nextHigherTier
      ? tierContentMap.get(nextHigherTier.tierId)
      : null;
    const isHighestTier = !nextHigherTier;

    const tierStatus =
      beResponse.currentTier.tierStatus === "ACCEPTED" && nextHigherTier
        ? nextHigherTier.tierStatus === "QUALIFIED"
          ? "QUALIFIED"
          : nextHigherTier.tierStatus || null
        : beResponse.currentTier.tierStatus;

    const isSameTierType =
      tierStatus === "ACCEPTED" || tierStatus === "REQUALIFY";

    Object.assign(result, {
      currentTier: currentTierContent?.title || false,
      tierToUnlock: isSameTierType
        ? currentTierContent?.title
        : nextHigherTierContent?.title || currentTierContent?.title,
      valueToUnlock: isSameTierType
        ? beResponse.currentTier.minimumSpend
        : nextHigherTier?.minimumSpend,
      displayedTierDate: formatDate(
        isSameTierType
          ? beResponse.currentTier.endDate
          : nextHigherTier?.endDate
      ),
      tierStatus: tierStatus,
      noTierTitle: contentData?.noTierTitle,
      pdfPath: currentTierContent?.pdfPath,
      currentTierCode: beResponse?.currentTier?.tierCode,
      currentTierId: beResponse?.currentTier?.tierId,
      currentTierViewBenefitsId: beResponse?.currentTier?.tierId - 1,
      currentDiscountCode: beResponse?.currentTier?.discountCode,
      currentTierEndDate: beResponse?.currentTier?.endDate,
      currentTierManaged: beResponse?.currentTier?.managedTier,
    });

    beResponse?.tierLevels?.forEach((tierLevel) => {
      const contentItem = createContentItem(tierLevel);
      if (contentItem) result.contentData.push(contentItem);
    });

    return result;
  };

  const formattedData = beResponse
    ? transformDiscountData(beResponse, staticContent.discountsData)
    : null;

  return (
    <div className={classes.discountsPage}>
      <Container className={classes.headerContainer}>
        <Row>
          <Col xs={12} className="gx-0">
            <h1 className={classes.headerTitle}>
              <FormattedMessage id="discounts.title" />
            </h1>
            <h3 className={classes.headerSubtitle}>
              <FormattedMessage id="discounts.subtitle" />
            </h3>
          </Col>
        </Row>
      </Container>
      {beResponse !== null && staticContent.discountsData ? (
        <DiscountsContent
          data={formattedData}
          onSuccess={fetchDiscounts}
          beResponse={beResponse}
        />
      ) : (
        <DiscountsSkeleton />
      )}
    </div>
  );
};

export default Discounts;
